import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getPatientsStats, PatientManagementDataPayload } from '../../services';
import {
  IconUser,
  IconCircleDashedCheck,
  IconDeviceDesktop,
  IconChecks
} from '@tabler/icons-react';
import { colors } from '../../constants';

export interface StatCard {
  count: { aggregate: { count: number } };
  label: string;
  infoIcon: JSX.Element;
  infoLabel: string;
}

export interface PatientsStatsResponse {
  totalPatients: { aggregate: { count: number } };
  verifiedPatients: { aggregate: { count: number } };
  verificationPendingPatients: { aggregate: { count: number } };
  activePatients: { aggregate: { count: number } };
}

export const usePatientStats = (payload: PatientManagementDataPayload) => {
  const { t } = useTranslation();

  const query = useQuery<PatientsStatsResponse>({
    queryKey: ['patient-stats', payload],
    queryFn: async () => {
      const result = await getPatientsStats(payload);
      return {
        totalPatients: result?.totalPatients ?? 0,
        verifiedPatients: result?.verifiedPatients ?? 0,
        verificationPendingPatients: {
          aggregate: result?.verificationPendingPatients ?? 0
        },
        activePatients: result?.activePatients ?? 0
      };
    },
    enabled: Boolean(payload?.startTimestamp && payload?.endTimestamp),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false
  });

  const patientStats: StatCard[] = useMemo(() => {
    if (!query.data) return [];

    // Type assertion to ensure TS knows the correct type
    const stats = query.data as PatientsStatsResponse;

    return [
      {
        count: stats.totalPatients,
        label: t('patientsManagement.stats.totalPatients.label'),
        infoIcon: <IconUser size={15} color={colors.blueDarkText} stroke={1} />,
        infoLabel: t('patientsManagement.stats.totalPatients.infoLabel')
      },
      {
        count: stats.verifiedPatients,
        label: t('patientsManagement.stats.verifiedPatients.label'),
        infoIcon: <IconCircleDashedCheck size={15} color={colors.blueDarkText} stroke={1} />,
        infoLabel: t('patientsManagement.stats.verifiedPatients.infoLabel')
      },
      {
        count: stats.verificationPendingPatients,
        label: t('patientsManagement.stats.verificationPendingPatients.label'),
        infoIcon: <IconDeviceDesktop size={15} color={colors.blueDarkText} stroke={1} />,
        infoLabel: t('patientsManagement.stats.verificationPendingPatients.infoLabel')
      },
      {
        count: stats.activePatients,
        label: t('patientsManagement.stats.activePatients.label'),
        infoIcon: <IconChecks size={15} color={colors.blueDarkText} stroke={1} />,
        infoLabel: t('patientsManagement.stats.activePatients.infoLabel')
      }
    ];
  }, [query.data, t]);

  return {
    ...query,
    patientStats
  };
};
